import React from "react";
import styled, { css } from "styled-components";
import { staticText } from "../../shared/statics/static-text";
import {
  maxLarge,
  maxMedium,
  maxSmall,
  maxXSmall,
  minLarge,
  minMedium,
  minSmall,
  minXLarge,
  shade0,
  shade300,
  shade700,
  shade900,
} from "../../styles/tokens";
import { normalStyles, smallStrongStyles } from "../body/Body.Parts";
import { Icon } from "./sort-icon/SortIcon.Parts";

// TEXT
export const text = staticText.selection;

// STYLES
export const ScrollWrapper = styled.div<{ $maxHeight?: string }>`
  ${({ $maxHeight }) =>
    $maxHeight &&
    css`
      max-height: ${$maxHeight};
      overflow-y: auto;
    `}
`;

export const Container = styled.table`
  width: 100%;
  display: table;
  border-collapse: collapse;
  table-layout: fixed;
`;

export const TableBody = styled.tbody`
  display: table-row-group;
  vertical-align: middle;
`;

export const TableHead = styled.thead<{ $sticky?: boolean }>`
  display: table-header-group;
  vertical-align: middle;

  ${({ $sticky }) =>
    $sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: 1;
    `}
`;

export const TableCell = styled.td<TableCellProps>`
  display: ${({ $showMediaQuery }) =>
    $showMediaQuery ? "none" : "table-cell"};
  vertical-align: inherit;
  align-items: center;
  min-height: 4rem;
  padding: 1rem;
  word-break: break-word;
  ${normalStyles}

  ${({ $width: $size }) => {
    // If $size is a custom object, cascade values through breakpoints
    if (typeof $size === "object") {
      const xsmall = $size.xSmall || "auto";
      const small = $size.small || xsmall;
      const medium = $size.medium || small;
      const large = $size.large || medium;
      const xlarge = $size.xLarge || large;

      return css`
        @media ${maxSmall} {
          width: ${xsmall};
          padding: 1rem 0;
        }
        @media ${minSmall} {
          width: ${small};
        }
        @media ${minMedium} {
          width: ${medium};
        }
        @media ${minLarge} {
          width: ${large};
        }
        @media ${minXLarge} {
          width: ${xlarge};
        }
      `;
    }

    if (typeof $size === "string")
      return css`
        width: ${$size};
      `;

    return css`
      width: min-content;
    `;
  }}

  ${({ $showMediaQuery }) =>
    $showMediaQuery &&
    `@media ${$showMediaQuery} {
      display: table-cell;
    }`}
`;

export const TableHeaderCell = styled(TableCell)<TableHeaderCellProps>`
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  cursor: ${({ $sortable }) => $sortable && "pointer"};
  ${smallStrongStyles};
  > div {
    > ${Icon} {
      fill: ${({ $active }) => $active && shade0};
    }
  }

  &:hover,
  &:focus {
    > div {
      > ${Icon} {
        background: ${({ $active }) => ($active ? shade700 : "transparent")};
        fill: ${({ $active }) => ($active ? shade0 : shade700)};
      }
    }
  }

  &:focus:not(:focus-visible, :hover) {
    > div {
      > ${Icon} {
        fill: ${({ $active }) => !$active && "transparent"};
        background: transparent;
      }
    }
  }

  &:focus-visible:not([disabled]) {
    box-shadow: inset 0 0 0 1px ${shade0};
  }
`;

export const TableHeaderCellContent = styled.div`
  min-height: 1.5rem;
  min-width: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  color: ${shade300};
`;

export const TableRow = styled.tr<TableRowProps>`
  width: 100%;
  display: table-row;
  vertical-align: inherit;
  text-decoration: none;
  color: ${shade0};
  table-layout: fixed;
  font-size: 1rem;

  &:hover,
  &:focus {
    background: ${({ $clickable }) => $clickable && shade900};
    cursor: ${({ $clickable }) => $clickable && "pointer"};
  }

  &:focus-visible:not([disabled]) {
    box-shadow: inset 0 0 0 1px ${shade0};
  }

  @media ${minMedium} {
    --table-row-actions-color: ${shade700};

    ${({ $clickable }) =>
      $clickable &&
      css`
        &:hover {
          --table-row-actions-color: ${shade0};
        }
      `}
  }
`;

export const TableRowActions = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-end;
  --icon-button-base-color: var(--table-row-actions-color);
`;

export const TableHeadRow = styled(TableRow)`
  background-color: ${shade900};
  font-weight: bold;
  font-size: 1rem;

  button {
    margin-left: 0.5rem;
  }
`;

// DATA

// TYPES
export type TableData = {
  id: string | number;
};

type BaseTableColumn = {
  label?: React.ReactNode;
  showMediaQuery?:
    | typeof maxXSmall
    | typeof maxSmall
    | typeof minSmall
    | typeof minMedium
    | typeof maxMedium
    | typeof minLarge
    | typeof maxLarge
    | typeof minXLarge;
  width?: string | CustomWidth;
};

export type TableColumnCommon<DataType extends TableData> = BaseTableColumn & {
  dataKey: keyof DataType;
  key?: never;
  sortable?: boolean;
  render?: (row: DataType) => React.ReactNode;
};

export type TableColumnCustom<
  DataType extends TableData,
  CustomColumnKeys extends string = string,
> = BaseTableColumn & {
  key: keyof DataType | CustomColumnKeys;
  dataKey?: never;
  sortable?: never;
  sorts?: keyof DataType;
  render: (row: DataType) => React.ReactNode;
};

export type TableColumn<
  DataType extends TableData,
  CustomColumnKeys extends string = string,
> = TableColumnCommon<DataType> | TableColumnCustom<DataType, CustomColumnKeys>;

export type SortState<
  DataType extends TableData = TableData & Record<string, unknown>,
> = {
  direction: SortDirection;
  key?: keyof DataType;
};

export type Props<
  DataType extends TableData,
  CustomColumnKeys extends string = string,
> = {
  /** The data structure populating the table */
  data: DataType[];
  /** Each column of the table with its own respective settings and custom functions */
  columns: TableColumn<DataType, CustomColumnKeys>[];
  /** Change handler that receives new sort settings to sort the data by */
  onSortChange?: (settings: SortState<DataType>) => void;
  /** The current sort settings the table is set to  */
  sortState?: SortState<DataType>;
  /** Change handler that receives the data item matching the row that was clicked on */
  onRowClick?: (item: DataType) => void;
  /** Customize props per single row of the table (not called for the header row) */
  getRowProps?: (
    props: React.ComponentPropsWithoutRef<typeof TableRow>,
    row: DataType,
  ) => React.ComponentPropsWithoutRef<typeof TableRow>;
  /** Whether to hide the header row */
  hideHeader?: boolean;
  /** Maximum height for the table body, enabling vertical scrolling with a sticky header (e.g. "400px", "50vh") */
  scrollableMaxHeight?: string;
  /** Currently-selected row ids (controlled — pair with onSelectedIdsChange) */
  selectedIds?: DataType["id"][];
  /** Providing this enables a leading checkbox column + header select-all */
  onSelectedIdsChange?: (ids: DataType["id"][]) => void;
  /** Shows the checkbox column as non-interactive skeletons, for loading states */
  selectionSkeleton?: boolean;
};

type TableCellProps = {
  $showMediaQuery?:
    | typeof maxXSmall
    | typeof maxSmall
    | typeof minSmall
    | typeof minMedium
    | typeof maxMedium
    | typeof minLarge
    | typeof maxLarge
    | typeof minXLarge;
  $width?: string | CustomWidth;
};

type TableHeaderCellProps = { $active?: boolean; $sortable?: boolean };

type TableRowProps = {
  $clickable?: boolean;
};

export enum SortDirection {
  ASCENDING = "ASCENDING",
  DESCENDING = "DESCENDING",
  NONE = "NONE",
}

type CustomWidth = {
  xSmall?: string;
  small?: string;
  medium?: string;
  large?: string;
  xLarge?: string;
};

// CONSTANTS
export const CHECKBOX_COLUMN_WIDTH = "3.5rem";

// FUNCTIONS
export const isCustomTableColumn = <
  DataType extends TableData,
  CustomColumnKeys extends string = string,
>(
  column: TableColumn<DataType, CustomColumnKeys>,
): column is TableColumnCustom<DataType, CustomColumnKeys> => {
  return (
    (column as TableColumnCustom<DataType, CustomColumnKeys>).key !== undefined
  );
};

export const columnToKey = <DataType extends TableData>(
  column: TableColumn<DataType>,
) => {
  return toKey(isCustomTableColumn(column) ? column.key : column.dataKey);
};

export const toKey = (key: string | number | symbol): string | number => {
  return typeof key === "symbol" ? key.toString() : key;
};

export const defaultGetRowProps = <T>(props: T) => {
  return props;
};

// Handle 'enter' key down functionality on table row and header cells
export const handleKeyDown = (
  event:
    | React.KeyboardEvent<HTMLTableRowElement>
    | React.KeyboardEvent<HTMLTableCellElement>,
  callback?: () => void,
) => {
  if (callback && ["Enter", " "].includes(event.key)) {
    event.preventDefault();
    callback();
  }
};

// ASSETS
